import { hoverHelp } from "./hoverHelp.ts";
function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

const LOCK_SVG =
  '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><rect x="4.5" y="9" width="11" height="8" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M7 9V6.5a3 3 0 016 0V9" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
const UNLOCK_SVG =
  '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><rect x="4.5" y="9" width="11" height="8" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M7 9V6.5a3 3 0 015.8-1" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';

export interface AutoParCellsPresentation {
  name: string;
  used: string | null;
  history: string;
  insufficientHistory: boolean;
  unit: string;
  variant: string;
  tag: { text: string; kind: string };
  auto: string;
  held: string;
  mayManage: boolean;
  locked: boolean;
  unavailableReason?: string;
  autoMissing: boolean;
  onAccept: () => void;
  onLock: () => void;
}
export function createAutoParCells(
  model: AutoParCellsPresentation,
  idPrefix = "admin-autopar",
): { auto: HTMLElement; used: HTMLElement; lock: HTMLElement } {
  const ID = idPrefix;
  const used = node("div", "ap-rate");
  used.dataset["testid"] = `${ID}-used`;
  if (model.used === null) {
    used.append(node("b", "ap-na", "–"));
    const history = model.history;
    if (model.insufficientHistory && history !== "") used.append(node("span", null, history));
  } else {
    used.append(node("b", null, model.used), node("span", null, model.unit));
  }

  const auto = node("div", "ap-auto");
  auto.dataset["testid"] = `${ID}-auto`;
  const tag = model.tag;
  if (model.variant === "would_be") {
    auto.append(node("span", "ap-na ap-would", tag.text), node("b", null, model.auto));
  } else if (model.variant === "na") {
    auto.append(node("b", "ap-na", "–"));
  } else if (model.variant === "held") {
    auto.append(node("b", null, model.auto));
    const held = node("span", "ap-delta ap-delta-held", tag.text);
    held.dataset["testid"] = `${ID}-tag`;
    auto.append(held);
    const accept = node("button", "btn-secondary ap-accept", "Accept");
    accept.type = "button";
    accept.dataset["testid"] = `${ID}-accept`;
    accept.disabled = !model.mayManage;
    accept.setAttribute("aria-label", `Accept the held par of ${model.held} for ${model.name}`);
    accept.addEventListener("click", model.onAccept);
    auto.append(accept);
  } else {
    auto.append(node("b", null, model.auto));
    const delta = node("span", `ap-delta ap-delta-${tag.kind}`, tag.text);
    delta.dataset["testid"] = `${ID}-tag`;
    auto.append(delta);
  }

  if (model.unavailableReason !== undefined) {
    hoverHelp(
      model.autoMissing ? auto : used,
      model.unavailableReason,
      "Used in automatic par calculations.",
      "A configured vendor and sufficient usage history allow a recommendation.",
    );
  }

  const lock = node("button", model.locked ? "ap-lock ap-lock-on" : "ap-lock");
  lock.type = "button";
  lock.dataset["testid"] = `${ID}-lock`;
  lock.setAttribute("aria-pressed", String(model.locked));
  lock.setAttribute("aria-label", `Keep my par for ${model.name}`);
  lock.innerHTML = model.locked ? LOCK_SVG : UNLOCK_SVG;
  lock.disabled = !model.mayManage;
  lock.addEventListener("click", model.onLock);

  return { auto, used, lock };
}
